import { getScaToken } from '../auth/getScaToken';
import { getAuthBaseUrl, getSpectrumAuthBaseUrl } from '../auth/ssoClientConfig';
import { defaultBaseUrl, defaultBaseUrlStaging, defaultSpectrumBaseUrl } from './constants';
import { AuthStatus, getAuthState, getSsoClientId } from '../auth/authState';
import { getCollectionMode } from '../environment';
import { CollectionMode } from '../initBlizzard';
import { getLogger } from '../logger';

// Try to get the upload config, or undefined if an error was thrown.
/** - TODO(kho): TSDoc */
export async function tryToGetUploadConfig(
  refresh?: UploadConfig
): Promise<UploadConfig | undefined> {
  let uploadConfig: UploadConfig | undefined;
  try {
    uploadConfig = await getUploadConfig(refresh);
  } catch (e) {
    log.error('Failed to get upload config: ', e);
    uploadConfig = undefined;
  }
  return uploadConfig;
}

/**
 * Retrieve the upload configuration based on the current authentication state.
 *
 * @param refresh If provided, will attempt to refresh the provided {@link UploadConfig}, i.e. if the
 *   authentication token is no longer valid. This is only applicable to SCA auth.
 * @internal
 * @see getAuthState
 */
async function getUploadConfig(refresh?: UploadConfig): Promise<UploadConfig> {
  const authState = await getAuthState();
  const ssoClientId = getSsoClientId();

  // Should we use an authenticated route?
  const isAuthEnabled =
    ssoClientId && authState.ssoToken && authState.authStatus === AuthStatus.AUTHENTICATED;

  if (refresh && isAuthEnabled) {
    // We can't refresh SSO auth because the auth provider provides it to us asynchronously. If we hit a auth error
    // then it means the upstream provider did not correctly refresh auth
    log.info(
      'SSO tokens cannot be refreshed - if provider does not provide a new one asynchronously this may be an issue with the provider'
    );
    return refresh;
  }

  // If authenticated, use the provided SSO token.
  // If we are unauthenticated, we need to retrieve an SCA token to call the unauthenticated path with.
  // If the previous request failed auth, we might need to refresh that SCA token (note that there's nothing we can do
  // if we were authenticated but the request failed - it's the responsibility of the auth provider to give us a new
  // token when it has it).
  const authorizationHeader = isAuthEnabled
    ? `Bearer ${authState.ssoToken}`
    : `SCA ${await getScaToken(Boolean(refresh))}`;

  const baseUrl = isAuthEnabled ? getAuthBaseUrl(ssoClientId) : getUnauthenticatedBaseUrl();

  const spectrumBaseUrl = isAuthEnabled
    ? getSpectrumAuthBaseUrl(ssoClientId)
    : defaultSpectrumBaseUrl;

  return {
    baseUrl,
    spectrumBaseUrl,
    credentials: isAuthEnabled ? 'include' : 'same-origin',
    authorizationHeader,
  };
}

/** Retrieve the base URL for unauthenticated requests. */
export function getUnauthenticatedBaseUrl(): string {
  return getCollectionMode() === CollectionMode.STAGE ? defaultBaseUrlStaging : defaultBaseUrl;
}

interface UploadConfig {
  baseUrl: string;
  spectrumBaseUrl: string;
  credentials: RequestCredentials;
  authorizationHeader: string;
}

const log = getLogger('uploadConfig');
